/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { Component } from 'react';
import {
    Icon,
    Button,
    RequiredValidation,
    ValidationMessage,
    FlexRow,
    FlexCol,
    Text,
    StyledModal,
} from 'sarsaparilla';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { ARE_CHOICES_SELECTED, IS_CHOICE_DATE_EMPTY } from 'ui-permit/src/constants';
import * as Shapes from 'ui-permit/src/props/permit';
import TempForm from './TempForm';
import PreferredDatesListContainer from '../../containers/LotteryAvailabilityContainer';
import { dateSelectionProps } from '../../props/DateSelection';
import getUniqueId from '../../utils/getUniqueId';

const propTypes = {
    permit: Shapes.permitProps,
    divisions: Shapes.divisionProps,
    seasonStart: PropTypes.string,
    seasonEnd: PropTypes.string,
    dates: PropTypes.arrayOf(dateSelectionProps),
    onChange: PropTypes.func.isRequired,
    max: PropTypes.number,
    isCommercialUser: PropTypes.bool,
    pActions: PropTypes.shape({
        updatePendingReservation: PropTypes.func.isRequired,
        formValidationChange: PropTypes.func.isRequired,
    }).isRequired,
    validation: PropTypes.object.isRequired,
    displayValidationError: PropTypes.bool,
    seasonYear: PropTypes.number,
    permitType: PropTypes.string,
};

const defaultProps = {
    max: 4,
};

export default class PreferredDatesPicker extends Component {
    constructor(props) {
        super(props);

        this.state = {
            datesSelected: 1,
            modalIsOpen: false,
        };
        this.handleNumberPicked = this.handleNumberPicked.bind(this);
        this.handleNumberChanged = this.handleNumberChanged.bind(this);
        this.addSelection = this.addSelection.bind(this);
        this.removeSelection = this.removeSelection.bind(this);
        this.handleSelectionChange = this.handleSelectionChange.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.openModal = this.openModal.bind(this);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.dates.length > this.props.dates.length) {
            this.resetValidations();
        }
    }

    onFormValidate = (isInValid, error, path) => {
        if (isInValid) {
            this.props.pActions.formValidationChange(
                `${this.props.reservation.id}_${path}`,
                error
            );
        } else {
            this.props.pActions.formValidationChange(
                `${this.props.reservation.id}_${path}`,
                null
            );
        }
    };

    onChoiceSelectedValidated = (isInValid, error) =>
        this.onFormValidate(isInValid, error, ARE_CHOICES_SELECTED);

    onDateItemSelectedValidated = (index) => (isInvalid, error) =>
        this.onFormValidate(isInvalid, error, `${IS_CHOICE_DATE_EMPTY}_${index}`);

    getNewSelection() {
        return { id: getUniqueId() };
    }

    handleNumberPicked() {
        const selection = [];
        for (let i = 0; i < this.state.datesSelected; i++) {
            selection.push(this.getNewSelection());
        }
        this.props.onChange(selection);
    }

    addSelection(e) {
        e.preventDefault();
        this.props.onChange([...this.props.dates, this.getNewSelection()]);
    }

    removeSelection(index) {
        const selections = this.props.dates.slice();
        selections.splice(index, 1);
        this.props.onChange(selections);
        // this.resetValidations(selections);
    }

    resetValidations = () => {
        const { dates: selections } = this.props;
        const defaultMessage = (i) => (
            <ValidationMessage
                controlName={`Choice #${i + 1} A Valid date`}
                message=" is required."
            />
        );

        selections.forEach((s, i) => {
            const isInvalid = !s.preferred_date;
            this.onFormValidate(
                isInvalid,
                defaultMessage(i),
                `${IS_CHOICE_DATE_EMPTY}_${i}`
            );
        });

        // clear the remaining date
        if (selections.length) {
            this.onFormValidate(
                false,
                defaultMessage(selections.length),
                `${IS_CHOICE_DATE_EMPTY}_${selections.length}`
            );
        }
    };

    handleSelectionChange(index, value) {
        const selections = this.props.dates.slice();
        selections[index] = value;
        this.props.onChange(selections);
    }

    handleNumberChanged(n) {
        const max = this.props.max || defaultProps.max;
        let i = n;
        if (n > max) {
            i = max;
        }
        if (n < 0) {
            i = 0;
        }
        this.setState({ datesSelected: i });
    }

    openModal() {
        this.setState({ modalIsOpen: true });
    }

    closeModal() {
        this.setState({
            modalIsOpen: false,
        });
    }

    renderChoiceHelpModal() {
        return (
            <StyledModal
                size="md"
                heading="How do the lottery choices work?"
                isOpen={this.state.modalIsOpen}
                onRequestClose={this.closeModal}
            >
                <Text>
                    You can enter several choices. The order of your choices matter. When
                    evaluating your application, the system will start with your first
                    choice. If it finds availability with that choice, it will award a
                    permit reservation. If it doesn&apos;t find availability, the system
                    moves to the next choice. If no availability is found with any choice,
                    it will consider the application unsuccessful.
                </Text>
            </StyledModal>
        );
    }

    render() {
        const { validation, reservation, displayValidationError, permitType } =
            this.props;
        const selectionValidation = !isEmpty(this.props.dates) ? 'true' : '';
        const choicesInvalid =
            get(validation, `${reservation.id}_${ARE_CHOICES_SELECTED}`) &&
            displayValidationError;
        const max = this.props.max || defaultProps.max;
        let body;
        if (this.props.dates && this.props.dates.length > 0) {
            body = (
                <PreferredDatesListContainer
                    onValidatedDateItems={this.onDateItemSelectedValidated}
                    displayValidationError={displayValidationError}
                    permit={this.props.permit}
                    selections={this.props.dates}
                    allowMore={this.props.dates.length < max}
                    onChange={this.handleSelectionChange}
                    onAdd={this.addSelection}
                    onRemove={this.removeSelection}
                    seasonYear={this.props.seasonYear}
                    seasonStart={this.props.seasonStart}
                    seasonEnd={this.props.seasonEnd}
                    validation={this.props.validation}
                    reservation={this.props.reservation}
                    isCommercialUser={this.props.isCommercialUser}
                    pActions={this.props.pActions}
                    isLottery={this.props.isLottery}
                    permitType={permitType}
                />
            );
        } else {
            body = (
                <TempForm
                    onSubmit={this.handleNumberPicked}
                    onChange={this.handleNumberChanged}
                    value={this.state.datesSelected !== 0 ? this.state.datesSelected : 1}
                    isInvalid={choicesInvalid}
                    minValue={1}
                    maxValue={max}
                    isLabelRequired
                />
            );
        }

        return (
            <div className="per-preferred-dates-form per-section-group">
                <FlexRow justifyContent="space-between" className="mt-2 mx-0">
                    <FlexCol xs="variable">
                        <Text size="xl" fontWeight="semibold">
                            Lottery Preferences
                        </Text>
                    </FlexCol>
                    <FlexCol xs="variable">
                        <Button
                            appearance="link"
                            iconBeforeElement={<Icon iconName="help" />}
                            onClick={() => {
                                this.openModal();
                            }}
                        >
                            How do the choices work?
                        </Button>
                    </FlexCol>
                </FlexRow>
                <fieldset>
                    <legend>
                        <p id="preferred-dates-text">
                            Choose your preferred dates below. You can apply up to{' '}
                            <b>{max} choices</b>. The more dates you add the better your
                            chances at winning the lottery!
                        </p>
                    </legend>
                    {body}
                    <RequiredValidation
                        value={selectionValidation}
                        displayInSummary
                        onValidated={this.onChoiceSelectedValidated}
                        controlLabel="Lottery Choices"
                    />
                </fieldset>
                {this.renderChoiceHelpModal()}
            </div>
        );
    }
}

PreferredDatesPicker.propTypes = propTypes;
PreferredDatesPicker.defaultProps = defaultProps;
